import {Alert, Button, Paper, TextField, Typography} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {LoginRequest, useLoginMutation, useSessionQuery} from "../../app/api/api";
import {ReactComponent as Logo} from "../../assets/logo.svg";
import {useTranslation} from "react-i18next";

interface LoginState {
    from?: string;
}

export default function Login() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {data: session, isError, isFetching} = useSessionQuery();
    const [login, {isLoading}] = useLoginMutation();
    const [error, setError] = useState("");
    const location = useLocation();

    // Decide where to navigate to after login.
    const from = (location.state as LoginState)?.from || "/";

    // If logged in on another tab, navigate away from login screen.
    useEffect(() => {
        if (!isError && !isFetching && session) {
            navigate(from);
        }
    }, [session, isError, isFetching]);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const formData = new FormData(event.currentTarget);

        const data = {
            email: formData.get("email") as string,
            password: formData.get("password") as string,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        } as LoginRequest;

        try {
            await login(data).unwrap();
            navigate(from);
        } catch (e) {
            setError(t("session_loginFailed"));
        }
    };

    return (
        <form onSubmit={onSubmit}>
            <Paper
                elevation={4}
                sx={{
                    width: {
                        sm: 400,
                    },
                    mx: {
                        xs: 2,
                        sm: "auto",
                    },
                    my: {
                        xs: 2,
                        sm: 6,
                    },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 4,
                }}
            >
                <Logo />
                <Typography align="center" variant="h6" component="div" sx={{mt: 1, mb: 2}} gutterBottom>
                    Management
                </Typography>

                <TextField label="Email" name="email" variant="standard" fullWidth margin="normal" autoFocus />
                <TextField
                    sx={{mb: 2}}
                    label={t("generic_password")}
                    name="password"
                    variant="standard"
                    fullWidth
                    type="password"
                    margin="normal"
                />

                {error && (
                    <Alert sx={{my: 1}} severity="error">
                        {error}
                    </Alert>
                )}

                <Button type="submit" sx={{mt: 2, width: 200}} variant="contained" size="large" disabled={isLoading}>
                    {t("generic_logIn")}
                </Button>
            </Paper>
        </form>
    );
}
